import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Clock } from 'lucide-react';

function useCardsPerView() {
  const [cards, setCards] = useState(3);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setCards(1);
      else if (w < 1024) setCards(2);
      else setCards(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return cards;
}

export default function EventsCarousel({ events = [] }) {
  const cardsPerView = useCardsPerView();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const maxIndex = Math.max(0, events.length - cardsPerView);

  useEffect(() => { setIndex((i) => Math.min(i, maxIndex)); }, [maxIndex]);

  useEffect(() => {
    if (paused || events.length <= cardsPerView) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [paused, maxIndex, events.length, cardsPerView]);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(maxIndex, i + 1));

  const onKey = (e) => {
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
  };

  if (!events.length) {
    return (
      <div className="card-moj p-10 text-center text-moj-purple-deep">
        <Calendar size={36} className="mx-auto mb-3 text-moj-gold-deep"/>
        <p className="font-serif text-lg">New events coming soon. Stay blessed!</p>
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKey}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label="Upcoming church events"
      data-testid="events-carousel"
    >
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out gap-6"
          style={{ transform: `translateX(calc(-${index} * (100% / ${cardsPerView}) - ${index * 24 / cardsPerView}px))` }}
        >
          {events.map((ev) => (
            <article
              key={ev.id || ev.slug}
              className="card-moj flex-shrink-0 overflow-hidden flex flex-col"
              style={{ flexBasis: `calc((100% - ${(cardsPerView - 1) * 24}px) / ${cardsPerView})` }}
              data-testid={`event-card-${ev.slug}`}
            >
              <img
                src={ev.poster_url || 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1200&h=900&fit=crop'}
                alt={ev.poster_alt_text || `Poster for ${ev.title}`}
                className="poster-43"
                loading="lazy"
              />
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-moj-purple-deep leading-tight mb-3">{ev.title}</h3>
                <ul className="space-y-1.5 text-sm text-moj-charcoal mb-4 flex-1">
                  <li className="flex items-center gap-2"><Calendar size={14} className="text-moj-gold-deep"/> {ev.event_date}</li>
                  <li className="flex items-center gap-2"><Clock size={14} className="text-moj-gold-deep"/> {ev.event_time}</li>
                  <li className="flex items-start gap-2"><MapPin size={14} className="text-moj-gold-deep mt-0.5"/> <span>{ev.location}</span></li>
                </ul>
                {ev.rsvp_enabled !== false && (
                  <Link to={`/events/${ev.slug}/rsvp`} className="btn-gold w-full justify-center" data-testid={`rsvp-${ev.slug}`}>
                    RSVP Now
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>

      {events.length > cardsPerView && (
        <>
          <button className="carousel-arrow left-2 sm:-left-4" onClick={goPrev} disabled={index === 0} aria-label="Previous events" data-testid="carousel-prev">
            <ChevronLeft size={22} />
          </button>
          <button className="carousel-arrow right-2 sm:-right-4" onClick={goNext} disabled={index === maxIndex} aria-label="Next events" data-testid="carousel-next">
            <ChevronRight size={22} />
          </button>
          <div className="flex justify-center gap-2 mt-6" role="tablist">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-moj-purple' : 'w-2.5 bg-moj-gold-deep/40 hover:bg-moj-purple/50'}`}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={i === index}
                role="tab"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
